import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLockBodyScrollOnTablet } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { MenuSearchResultBlockVariant, } from '@as-react/common-types';
import { ArrowLeftIcon, ArrowUpLeftIcon } from '@yonderland/andes-icons';
import { ListBox, SearchFieldAlpha, useMediaQueryMatchesOnDesktop, Popover, Box, IconButton, Stack, TextAlpha, getHighlightedText, } from '@yonderland/andes-react';
import { useRef, useEffect, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { bannerIds, blockTitles } from '../../constants/SearchConstants';
import { hasItems } from '../../util/ArrayUtil';
import EnumUtil from '../../util/EnumUtil';
import { appendSearchQueryToUrl, getKeywordsSuggestionsUrl } from '../../util/SearchUtil';
import { generateTestId } from '../../util/TestUtil';
import { MenuSearchBarcodeReader } from './MenuSearchBarcodeReader';
const TEST_ID = 'list-box';
export const NewMenuSearch = ({ createAdobeAnalyticsSetSearchTermAction, menuSearchItemGrouping, suggestionsParentLink, setSearchValue, searchValue, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const searchInputElementRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState();
    const [selectedGroupAndItem, setSelectedGroupAndItem] = useState();
    const [savedSearchValue, setSavedSearchValue] = useState(searchValue);
    const [suggestedSearchValue, setSuggestedSearchValue] = useState('');
    const [isSearchMenuActive, setIsSearchMenuActive] = useState(false);
    const hasSearchResults = !!menuSearchItemGrouping && hasItems(Object.keys(menuSearchItemGrouping));
    const [isInputFocused, setIsInputFocused] = useState(false);
    const { root, isShopLanding } = useAemConfig();
    const listerPageUrl = `${root}lister.html`;
    useEffect(() => {
        if (!containerRef.current)
            return;
        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => {
            observer.disconnect();
        };
    }, []);
    const handleInputChange = (searchValueInput) => {
        setSelectedGroupAndItem(undefined);
        setSearchValue(searchValueInput);
    };
    const onButtonBackClickHandler = () => {
        setIsInputFocused(false);
        setIsSearchMenuActive(false);
    };
    const inputElementSetFocus = () => { var _a; return (_a = searchInputElementRef === null || searchInputElementRef === void 0 ? void 0 : searchInputElementRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    const onInputClearClickHandler = () => {
        setSearchValue('');
        setSavedSearchValue('');
        setSelectedGroupAndItem(undefined);
        setSuggestedSearchValue('');
    };
    const getMenuSearchResultBlockVariantItemByValue = (value) => {
        const currentSelectedKey = EnumUtil.getItemBasedOnValue(MenuSearchResultBlockVariant, value);
        return MenuSearchResultBlockVariant[currentSelectedKey];
    };
    const onButtonSearchClickHandler = () => {
        if (searchValue.length === 0) {
            inputElementSetFocus();
            setIsSearchMenuActive(true);
            return;
        }
        else {
            setIsSearchMenuActive(false);
        }
        if (selectedGroupAndItem) {
            const selectedMenuSearchItem = menuSearchItemGrouping[selectedGroupAndItem.type].items[selectedGroupAndItem.index];
            if (selectedGroupAndItem.type === MenuSearchResultBlockVariant.SUGGESTIONS) {
                createAdobeAnalyticsSetSearchTermAction(selectedMenuSearchItem.name.trim());
                window.location.href = selectedMenuSearchItem.url;
            }
            else {
                window.location.href = appendSearchQueryToUrl(selectedMenuSearchItem.url, searchValue);
            }
        }
        else {
            createAdobeAnalyticsSetSearchTermAction(searchValue);
            window.location.href = getKeywordsSuggestionsUrl(suggestionsParentLink, searchValue);
        }
    };
    const updateSuggestedSearchValue = (menuSearchItem) => {
        if (menuSearchItem) {
            const { name, subtitle } = menuSearchItem;
            const _subtitle = subtitle && subtitle.length > 0 ? subtitle.slice(0, 2).join(', ') : '';
            setSuggestedSearchValue(`${name} ${_subtitle}`.trim());
        }
        else {
            setSuggestedSearchValue('');
        }
    };
    const handleArrowDown = () => {
        var _a, _b;
        const currentSelectedTypeAsString = ((_a = selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.type) === null || _a === void 0 ? void 0 : _a.toString()) || Object.keys(menuSearchItemGrouping)[0];
        const currentSelectedType = getMenuSearchResultBlockVariantItemByValue(currentSelectedTypeAsString);
        const currentSelectedIndex = (selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.index) !== undefined ? selectedGroupAndItem.index : -1;
        // Check if last within group
        if (currentSelectedIndex + 1 === ((_b = menuSearchItemGrouping[currentSelectedType]) === null || _b === void 0 ? void 0 : _b.items.length)) {
            const availableTypes = Object.keys(menuSearchItemGrouping);
            // Check if not last group
            if (availableTypes.indexOf(currentSelectedType) < availableTypes.length - 1) {
                const newTypeIndex = availableTypes.indexOf(currentSelectedType) + 1;
                const newSelectedType = getMenuSearchResultBlockVariantItemByValue(availableTypes[newTypeIndex]);
                // Move to new group
                setSelectedGroupAndItem({
                    type: newSelectedType,
                    index: 0,
                });
                updateSuggestedSearchValue(menuSearchItemGrouping[newSelectedType].items[0]);
            }
            else {
                const firstSelectedType = getMenuSearchResultBlockVariantItemByValue(Object.keys(menuSearchItemGrouping)[0]);
                setSelectedGroupAndItem({ type: firstSelectedType, index: 0 });
                updateSuggestedSearchValue(menuSearchItemGrouping[firstSelectedType].items[0]);
            }
        }
        else {
            setSelectedGroupAndItem({
                type: currentSelectedType,
                index: currentSelectedIndex + 1,
            });
            updateSuggestedSearchValue(menuSearchItemGrouping[currentSelectedType].items[currentSelectedIndex + 1]);
        }
    };
    const handleArrowUp = () => {
        const availableTypes = Object.keys(menuSearchItemGrouping);
        if (selectedGroupAndItem) {
            const type = (selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.type) ||
                MenuSearchResultBlockVariant[availableTypes[0]];
            const index = (selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.index) || 0;
            // Check if first within group
            if (index === 0) {
                // Check if not first in group
                if (availableTypes.indexOf(type) > 0) {
                    const newTypeIndex = availableTypes.indexOf(type) - 1;
                    const newSelectedType = getMenuSearchResultBlockVariantItemByValue(availableTypes[newTypeIndex]);
                    // Move to new group
                    setSelectedGroupAndItem({
                        type: newSelectedType,
                        index: menuSearchItemGrouping[newSelectedType].items.length - 1,
                    });
                    updateSuggestedSearchValue(menuSearchItemGrouping[newSelectedType].items[menuSearchItemGrouping[newSelectedType].items.length - 1]);
                }
                else {
                    setSelectedGroupAndItem(undefined);
                    updateSuggestedSearchValue(undefined);
                }
            }
            else {
                setSelectedGroupAndItem({
                    type,
                    index: index - 1,
                });
                updateSuggestedSearchValue(menuSearchItemGrouping[type].items[index - 1]);
            }
        }
        else {
            // Move to the last group
            const newSelectedType = getMenuSearchResultBlockVariantItemByValue(availableTypes[availableTypes.length - 1]);
            const newSelectedIndex = menuSearchItemGrouping[newSelectedType].items.length - 1;
            setSelectedGroupAndItem({
                type: newSelectedType,
                index: newSelectedIndex,
            });
            updateSuggestedSearchValue(menuSearchItemGrouping[newSelectedType].items[newSelectedIndex]);
        }
    };
    const onInputKeyUpHandler = (e) => {
        const { key } = e;
        switch (key) {
            // ESCAPE
            case 'Esc': // IE/Edge specific value
            case 'Escape':
                !!e && e.preventDefault();
                onInputClearClickHandler();
                break;
            // ENTER
            case 'Enter':
                !!e && e.preventDefault();
                if (!searchValue) {
                    return;
                }
                onButtonSearchClickHandler();
                break;
            // ARROW_DOWN
            case 'Down': // IE/Edge specific value
            case 'ArrowDown':
                !!e && e.preventDefault();
                if (searchValue) {
                    handleArrowDown();
                }
                break;
            // ARROW_UP
            case 'Up': // IE/Edge specific value
            case 'ArrowUp':
                !!e && e.preventDefault();
                if (searchValue) {
                    handleArrowUp();
                }
                break;
            default:
                break;
        }
    };
    const searchItemClickHandler = (value) => {
        setSearchValue(`${value.name} `);
        inputElementSetFocus();
    };
    const onSubmitCatcher = (e) => {
        e.preventDefault();
    };
    const onBarcodeScanHandler = (barcode) => {
        window.location.href = appendSearchQueryToUrl(`${listerPageUrl}?input=scanner`, barcode);
    };
    useEffect(() => {
        if (savedSearchValue === '' && hasSearchResults) {
            inputElementSetFocus();
        }
    }, [savedSearchValue]);
    useEffect(() => setSavedSearchValue(searchValue), [searchValue]);
    useLockBodyScrollOnTablet(isSearchMenuActive);
    const handleSuggestClick = (e, elem) => {
        e.preventDefault();
        e.stopPropagation();
        searchItemClickHandler(elem);
    };
    const listBoxContent = (_jsx(ListBox, { testId: generateTestId({ testId: TEST_ID, suffix: 'block' }), children: Object.values(menuSearchItemGrouping).map((menuSearchItemGroup, index) => (_jsxs(Fragment, { children: [_jsxs(ListBox.Group, { testId: generateTestId({ testId: TEST_ID, suffix: 'group' }), analyticsAreaName: bannerIds[menuSearchItemGroup.type], children: [_jsx(ListBox.Label, { testId: generateTestId({ testId: TEST_ID, suffix: 'section-label' }), paddingInline: "5", paddingBlock: "2", children: t(blockTitles[menuSearchItemGroup.type]) }), menuSearchItemGroup.items.map((elem, i) => (_jsxs(ListBox.Item, { testId: generateTestId({ testId: TEST_ID, suffix: `item-${i}` }), href: elem.url || '#', state: !!selectedGroupAndItem &&
                                selectedGroupAndItem.type === menuSearchItemGroup.type &&
                                selectedGroupAndItem.index === i
                                ? 'selected'
                                : 'enabled', children: [_jsxs(Stack, { gap: "1", align: "center", testId: "result-item", children: [_jsx(TextAlpha, { strong: true, variant: "bodyM", color: "neutral", children: getHighlightedText(elem.name, searchValue, 'a:font-normal', 'search') }), elem.subtitle && elem.subtitle.length > 0 ? (_jsx(TextAlpha, { variant: "bodyS", color: "subtle", truncate: true, children: elem.subtitle.slice(0, 2).join(', ') })) : null] }), menuSearchItemGroup.enableSuggestionFill && !isDesktop ? (_jsx(IconButton, { variant: "plain", icon: { source: ArrowUpLeftIcon, size: 'sm' }, size: "sm", name: "right-icon-button", onClick: e => handleSuggestClick(e, elem) })) : null] }, `${menuSearchItemGroup.type}-${i}`)))] }), index < Object.keys(menuSearchItemGrouping).length - 1 && _jsx(ListBox.Separator, { paddingBlock: "3" })] }, `${menuSearchItemGroup.type}-block-${index}`))) }));
    const containerStyle = Object.assign({}, (!isDesktop && isInputFocused
        ? {
            position: 'fixed',
            inset: 0,
            zIndex: '550',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
        }
        : {}));
    const headerStyle = Object.assign({ position: 'relative', transitionProperty: 'all', transitionDuration: '300ms', transitionTimingFunction: 'ease-in-out' }, (!isDesktop && isInputFocused
        ? {
            padding: '1.25rem',
            backgroundColor: '#fff',
            transitionProperty: 'background-color',
            transitionDuration: '100ms',
        }
        : {}));
    return (_jsxs("form", { method: "GET", action: listerPageUrl, onSubmit: onSubmitCatcher, suppressHydrationWarning: true, style: { display: 'grid' }, children: [_jsxs("div", { ref: containerRef, style: containerStyle, children: [_jsx("div", { style: headerStyle, children: _jsx(SearchFieldAlpha, { input: {
                                testId: 'search-input',
                                innerRef: searchInputElementRef,
                                id: 'search',
                                name: 'q',
                                placeholder: t('flyout.search.placeholder'),
                                onFocus: () => {
                                    setIsInputFocused(true);
                                    if (!isSearchMenuActive)
                                        setIsSearchMenuActive(true);
                                },
                                onKeyUp: e => {
                                    onInputKeyUpHandler(e);
                                },
                                onClick: () => {
                                    if (!isInputFocused)
                                        setIsInputFocused(true);
                                },
                                adornmentStart: !isDesktop && isInputFocused ? (_jsx(Box, { paddingInlineEnd: "1", children: _jsx(IconButton, { variant: "plain", size: "sm", icon: {
                                            source: ArrowLeftIcon,
                                            color: 'neutral',
                                        }, onClick: onButtonBackClickHandler }) })) : undefined,
                            }, value: selectedGroupAndItem && suggestedSearchValue ? suggestedSearchValue : searchValue, autoSearch: true, onChange: handleInputChange, onClear: onInputClearClickHandler, hasBackDrop: isDesktop, onSubmitAdornmentClick: onButtonSearchClickHandler }, "search-input") }), !isDesktop && isInputFocused && _jsx(Box, { overflow: "auto", children: searchValue && listBoxContent })] }), isDesktop && (_jsx(Popover, { isOpen: hasSearchResults && isInputFocused && !!searchValue, onClose: () => setIsInputFocused(false), placement: "bottom-start", customWidth: containerWidth ? `${containerWidth}px` : undefined, slim: true, children: _jsx(Popover.Body, { children: _jsx(_Fragment, { children: hasSearchResults ? listBoxContent : null }) }) }, "search-popover")), isShopLanding && _jsx(MenuSearchBarcodeReader, { onScan: onBarcodeScanHandler, minLength: 9 })] }));
};
